import type { Router } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useOrgStore } from '@/stores/org';
import { useVisitedRoutesStore } from '@/stores/visitedRoutes';
import { DEFAULT_LANDING_ROUTE, isRouteHiddenInProd } from '@/config/featureFlags';

/**
 * Global navigation guard.
 *
 * - Routes with `meta.public === true` always pass (welcome, sign-in, forgot, etc.).
 * - Other routes require an authenticated session — if `auth.isAuthenticated`
 *   is false, refresh from the Amplify session once; if still not
 *   authenticated, route to /welcome (when no workspace is selected yet) or
 *   /sign-in (when an orgId is already pinned), with the original route
 *   stored as a `redirect` query param.
 */
export function installAuthGuard(router: Router): void {
  router.beforeEach(async (to) => {
    if (to.meta['public']) return true;
    const auth = useAuthStore();
    if (!auth.isAuthenticated) {
      const ok = await auth.refreshFromSession();
      if (!ok) {
        const org = useOrgStore();
        const dest = org.orgId ? 'sign-in' : 'welcome';
        return { name: dest, query: { redirect: to.fullPath } };
      }
    }
    // Provider-persona routes (e.g. Billing) are reachable only when the
    // tenant is an outsourcing service provider (claim-based `auth.isProvider`).
    // Direct-scope tenants are redirected to the default landing page rather
    // than shown an empty shell.
    if (to.meta['requiresProvider'] && !auth.isProvider) {
      return { name: DEFAULT_LANDING_ROUTE };
    }
    // Production-only hidden routes (src/config/featureFlags.ts) — the nav
    // entry is CSS-hidden, this blocks reaching the route by typed/bookmarked
    // URL. No-op outside production. DEFAULT_LANDING_ROUTE is never itself a
    // hidden route, so this can't loop.
    if (isRouteHiddenInProd(to.name?.toString())) {
      return { name: DEFAULT_LANDING_ROUTE };
    }
    return true;
  });

  // Record every successfully-navigated module route so the scope switcher can
  // warm-prefetch already-visited screens on a scope change.
  router.afterEach((to) => {
    if (to.meta['public']) return;
    useVisitedRoutesStore().markVisited(to.name?.toString() ?? null);
  });
}
